import { Grid } from '@mui/material';
import { HSpacer, RoutalPalette, RoutalTooltip, RoutalWarningIcon, VSpacer } from '@nx-smartmonkey/ui';
import { ReactElement } from 'react';
import styled from 'styled-components';
import DollarIcon from '../../../../../../assets/svg/Plan/analytics/costs/DollarIcon';
import MoneyIcon from '../../../../../../assets/svg/Plan/analytics/costs/MoneyIcon';
import RouteIcon from '../../../../../../assets/svg/Plan/analytics/costs/RouteIcon';
import StopsIcon from '../../../../../../assets/svg/Plan/analytics/costs/StopsIcon';
import { currencyToSymbol } from '../../../../../../constants/Pricing';
import { useRetrieveActiveProject } from '../../../../../../hooks/project/useRetrieveActiveProject';
import Text from '../../../../../../react-components/Text';
import { useRetrieveAnalyticsPlanCosts } from '../../../hooks/analytics/useRetrieveAnalyticsPlanCosts';
import { AnalyticsElement } from './AnalyticsElement';

const CostItem = styled(Grid)`
  display: flex;
  flex-direction: column !important;
  padding: 12px 20px;
  border-radius: 10px;
  background-color: #fff5cc;
  flex: 1;
`;

const CostItemIcon = styled(Grid)`
  display: flex;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 10px;
  align-items: center;
  border-radius: 10px;
  background-color: #ffd52d;
`;

interface PlanCostsElementProps {
  icon: ReactElement;
  value: number;
  currency: string;
  subtitleId: string;
}

const PlanCostsElement = ({ icon, value, currency, subtitleId }: PlanCostsElementProps) => {
  return (
    <CostItem>
      <CostItemIcon>{icon}</CostItemIcon>
      <Text style={{ textAlign: `center`, color: RoutalPalette.warning.dark }} variant="h5">
        {value}
        {currency}
      </Text>
      <div style={{ minHeight: `4px` }} />
      <Text style={{ textAlign: `center`, color: RoutalPalette.warning.medium }} variant="body" intlId={subtitleId} />
      <VSpacer large />
    </CostItem>
  );
};

export const PlanCosts = () => {
  const { data: activeProject } = useRetrieveActiveProject();
  const { totalCost, averageRouteCost, averageStopCost, vehiclesWithoutCost } = useRetrieveAnalyticsPlanCosts();

  if (!activeProject || !activeProject.units) {
    return null;
  }

  const currency = currencyToSymbol(activeProject.units.currency);

  return (
    <AnalyticsElement
      header={
        <>
          <Grid display="flex" flexDirection="row" alignItems="center">
            <DollarIcon style={{ width: `12px`, height: `18px` }} />
            <HSpacer small />
            <Text variant="h6" style={{ fontWeight: `600` }} intlId="planner.plan_details.analytics.costs.title" />
          </Grid>
          {vehiclesWithoutCost > 0 && (
            <RoutalTooltip
              id={`planner.plan_details.analytics.costs.warning`}
              overlayInnerStyle={{ padding: `4px` }}
              title={
                <Text
                  style={{ color: RoutalPalette.neutral02, textAlign: `center` }}
                  intlId="planner.plan_details.analytics.costs.warning"
                />
              }
              placement="top"
            >
              <RoutalWarningIcon color={RoutalPalette.error.medium} style={{ cursor: `pointer` }} />
            </RoutalTooltip>
          )}
        </>
      }
      headerStyle={{ justifyContent: `space-between` }}
      content={
        <Grid display="flex" flexDirection="column" width="100%">
          <PlanCostsElement
            icon={<MoneyIcon color={RoutalPalette.warning.dark} style={{ width: `16px`, height: `16px` }} />}
            value={totalCost}
            currency={currency}
            subtitleId={`planner.plan_details.analytics.costs.total_cost`}
          />

          <VSpacer small />

          <Grid display="flex" flexDirection="row" width="100%">
            <PlanCostsElement
              icon={<RouteIcon color={RoutalPalette.warning.dark} style={{ width: `18px`, height: `16px` }} />}
              value={averageRouteCost}
              currency={currency}
              subtitleId={`planner.plan_details.analytics.costs.average_route`}
            />
            <HSpacer small />
            <PlanCostsElement
              icon={<StopsIcon color={RoutalPalette.warning.dark} style={{ width: `16px`, height: `16px` }} />}
              value={averageStopCost}
              currency={currency}
              subtitleId={`planner.plan_details.analytics.costs.average_stop`}
            />
          </Grid>
        </Grid>
      }
    />
  );
};
